import { observable, action, computed } from 'mobx';

import Log from 'helpers/log';
import httpFacade from 'http/httpFacade';

import { BaseFormModel, isRequired, displayName } from 'stores/BaseForm';
import {
  TaskType,
  CreateTaskReqParams,
  IDataset,
  ArchitectureType,
  MLModel,
} from 'stores/Recognition/types';
import AppRouter from 'stores/AppRouter';
import { ITag } from 'components/Modals/TagsSelectModal/TagsSelectModal';
import { sortByAccessor } from 'helpers/accessors';

export const OptionsLimits = {
  stepsNumberMin: 0,
  stepsNumberMax: 200000,
  stepsNumberStep: 5000,
  durationMin: 0,
  durationMax: 480,
  durationStep: 15,
};

interface TaskParams {
  datasetIds: string[];
  detectorId: string;
}

class TaskEvalDetectorForm extends BaseFormModel {
  type: TaskType.EVAL_MODEL_DETECTOR;

  @observable
  selectedDatasets: ITag[] = [];

  @observable
  @displayName('DESCRIPTION*')
  @isRequired()
  description: string;

  @observable
  @displayName('DETECTOR_MODEL')
  @isRequired()
  detectorId: string;

  @observable loading = false;

  @observable datasets: IDataset[] = [];
  @observable models: MLModel[] = [];

  @computed
  get datasetIds() {
    return this.selectedDatasets.map(dataset => dataset.id);
  }

  @computed
  get datasetTags(): ITag[] {
    return this.datasets
      .map(dataset => ({
        id: dataset.id,
        title: dataset.name,
        date: dataset.createdDate,
      }))
      .sort(sortByAccessor({ desc: true, accessor: 'date' }));
  }

  constructor(type: TaskType.EVAL_MODEL_DETECTOR) {
    super();

    this.type = type;
  }

  @computed
  get isParamsValid() {
    return (
      Boolean(this.selectedDatasets.length) &&
      this.detectorId &&
      this.description
    );
  }

  @computed
  get modelsDetectionOptions() {
    return this.models
      .filter(model => model.architecture.type === ArchitectureType.DETECTION)
      .map(it => ({
        value: it.id,
        label: it.description,
      }));
  }

  @computed
  get requestParams(): CreateTaskReqParams<TaskParams> {
    return {
      type: this.type,
      description: this.description,
      params: {
        datasetIds: this.datasetIds,
        detectorId: this.detectorId,
      },
    };
  }

  @action.bound
  async init() {
    try {
      this.loading = true;

      const [datasets, models] = await Promise.all([
        httpFacade.recognition.fetchDatasets(),
        httpFacade.recognition.fetchModels(),
      ]);

      this.datasets = datasets.data;
      this.models = models.data;
    } catch (error) {
      Log.info(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  async onCreate() {
    try {
      this.loading = true;

      await httpFacade.recognition.createTask(this.requestParams);

      AppRouter.goBack();
    } catch (error) {
      Log.info(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  setDatasets(tags: ITag[]) {
    this.selectedDatasets = tags;
  }

  @action.bound
  removeDataset(id?: string) {
    this.selectedDatasets = id
      ? this.selectedDatasets.filter(it => it.id !== id)
      : [];
  }
}

export default TaskEvalDetectorForm;
